import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { ProspectImmobile, ProspectImmobiliGeoResponse } from "../../api";
import {
  addMapBaseLayers,
  drawImmobiliLayer,
  ensureMarkerIcons,
  FIT_MAX_ZOOM,
  highlightSelected,
  ITALY_CENTER,
  type MapEntry,
} from "./immobili-map-utils";

interface ImmobiliMapProps {
  geo: ProspectImmobiliGeoResponse | null;
  immobili: ProspectImmobile[];
  selectedIndex: number | null;
  onSelectIndex: (i: number | null) => void;
  loading?: boolean;
  error?: string | null;
  base?: "satellite" | "catasto";
}

export function ImmobiliMap({
  geo,
  immobili,
  selectedIndex,
  onSelectIndex,
  loading,
  error,
  base = "catasto",
}: ImmobiliMapProps) {
  const mapHostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const entriesRef = useRef<MapEntry[]>([]);
  // onSelectIndex viene memorizzato per non re-bindare le geometrie a ogni render.
  const onSelectRef = useRef(onSelectIndex);
  onSelectRef.current = onSelectIndex;

  const items = useMemo(() => geo?.items ?? [], [geo]);
  const counts = useMemo(() => {
    let parcel = 0;
    let geocoded = 0;
    for (const it of items) {
      if (it.parcel) parcel += 1;
      else if (it.centroid) geocoded += 1;
    }
    return { parcel, geocoded, localized: parcel + geocoded };
  }, [items]);

  // ── Init mappa (una volta) ──────────────────────────────────────────────
  useEffect(() => {
    if (!mapHostRef.current || mapRef.current) return;
    const host = mapHostRef.current;
    ensureMarkerIcons();
    const map = L.map(host, {
      center: ITALY_CENTER,
      zoom: 6,
      zoomControl: true,
    });
    addMapBaseLayers(map, base);
    mapRef.current = map;

    const refresh = () => map.invalidateSize();
    const raf = requestAnimationFrame(refresh);
    const t1 = window.setTimeout(refresh, 80);
    const t2 = window.setTimeout(refresh, 320);
    const ro = new ResizeObserver(refresh);
    ro.observe(host);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
      entriesRef.current = [];
    };
  }, [base]);

  // ── Disegna i poligoni quando cambiano gli items ────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) {
      layerRef.current.remove();
      layerRef.current = null;
      entriesRef.current = [];
    }
    if (items.length === 0) return;
    const { group, entries } = drawImmobiliLayer(map, items, (i) => onSelectRef.current(i));
    layerRef.current = group;
    entriesRef.current = entries;

    // Fit iniziale su tutte le geometrie risolte (bounds dal backend).
    if (geo?.bounds) {
      const b = L.latLngBounds(
        [geo.bounds.minLat, geo.bounds.minLon],
        [geo.bounds.maxLat, geo.bounds.maxLon],
      );
      map.fitBounds(b, { maxZoom: FIT_MAX_ZOOM, padding: [40, 40] });
    } else if (entries.length > 0) {
      const all = L.latLngBounds(entries.map((e) => e.bounds.getCenter()));
      map.fitBounds(all, { maxZoom: FIT_MAX_ZOOM, padding: [40, 40] });
    }
  }, [items, geo]);

  // ── Evidenzia + zoom sulla geometria selezionata ────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || entriesRef.current.length === 0) return;
    highlightSelected(map, entriesRef.current, selectedIndex);
  }, [items, selectedIndex]);

  const total = immobili.length;
  const breakdown =
    counts.localized === 0
      ? ""
      : counts.geocoded === 0
        ? " (particelle catastali)"
        : counts.parcel === 0
          ? " (da indirizzo)"
          : ` (${counts.parcel} catastali · ${counts.geocoded} da indirizzo)`;
  const subtitle = loading
    ? "caricamento mappa…"
    : error
      ? error
      : `${counts.localized}/${total} immobili localizzati${breakdown}`;

  return (
    <div className="col gap-6">
      <div className="row gap-8" style={{ alignItems: "baseline", flexWrap: "wrap" }}>
        <div className="k" style={{ fontWeight: 600 }}>{base==='catasto'?'Particelle':'Mappa immobili'}</div>
        <span
          className="muted"
          style={{ fontSize: 11, color: error ? "var(--danger)" : undefined }}
        >
          {subtitle}
        </span>
      </div>
      <div
        style={{
          position: "relative",
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid var(--line)",
        }}
      >
        <div ref={mapHostRef} className="property-map-host" />
        {!loading && !error && counts.localized === 0 && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.35)",
              color: "var(--text-2)",
              fontSize: 12,
              textAlign: "center",
              padding: 16,
              pointerEvents: "none",
            }}
          >
            Nessun immobile localizzato: né particella catastale né indirizzo geocodificabile.
          </div>
        )}
      </div>
    </div>
  );
}
